import CirclePattern from "./CirclePattern";

const FLOORPLAN_IMG =
  "https://customer-assets.emergentagent.com/job_thermal-design-3/artifacts/qpwkumq8_Picture6.png";

const APP_PREVIEW_IMG =
  "https://customer-assets.emergentagent.com/job_thermal-design-3/artifacts/emi0cbbg_image.png";

const APP_FEATURES = [
  "Whole-house system planning",
  "Room-by-room heat panel specification",
  "Zone-based heating design",
  "Accurate panel sizing and placement",
  "Fast, professional quotation support",
  "Installer and designer friendly interface",
  "Improved project consistency and efficiency",
];

// ---------- Sub-components ---------------------------------------------------

function DesignHeader() {
  return (
    <div className="max-w-4xl">
      <div className="overline mb-6">Design</div>
      <h2
        data-testid="design-headline"
        className="font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-[-0.02em] leading-[1.05] text-ecoh-ink"
      >
        Designed for specification.
        <br />
        <span>Built for delivery.</span>
      </h2>
      <p className="mt-10 text-ecoh-muted text-base md:text-lg font-light leading-relaxed max-w-3xl">
        ecoHeatwave is engineered to support modern architectural and
        development requirements by combining design flexibility, installation
        efficiency, and long-term system performance.
      </p>
    </div>
  );
}

function SpecificationSection() {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
      <div className="lg:col-span-5">
        <h3 className="font-display text-2xl md:text-3xl lg:text-4xl font-light tracking-[-0.02em] leading-[1.1] text-ecoh-ink">
          Precise specification from day one.
        </h3>
        <p className="mt-6 text-ecoh-muted text-base md:text-lg font-light leading-relaxed">
          Developed using scaled drawings and thermal performance data, each
          ecoHeatwave system is designed to meet the precise heating demand
          of individual rooms or zones. This enables accurate specification
          from early-stage design and supports coordinated planning across
          architectural and building services disciplines.
        </p>
      </div>
      <div className="lg:col-span-7">
        <div className="relative rounded-2xl overflow-hidden bg-white ring-1 ring-ecoh-ink/8 aspect-[4/3]">
          <img
            src={FLOORPLAN_IMG}
            alt="ecoHeatwave heating layout overlaid on a typical UK new-build floor plan"
            data-testid="design-floorplan-image"
            className="absolute inset-0 w-full h-full object-contain p-4 md:p-6 transition-transform duration-700 hover:scale-[1.02]"
          />
        </div>
        <p className="mt-3 text-xs tracking-[0.2em] uppercase text-ecoh-muted">
          Scaled drawing · Per-room heating layout
        </p>
      </div>
    </div>
  );
}

function EstimateRequest() {
  return (
    <div className="mt-16 md:mt-20">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
        <div className="lg:col-span-5">
          <h3
            data-testid="design-estimate-headline"
            className="font-display text-2xl md:text-3xl lg:text-4xl font-light tracking-[-0.02em] leading-[1.1] text-ecoh-ink"
          >
            Request an initial design and cost estimate.
          </h3>
          <p className="mt-4 text-ecoh-ink/70 text-base md:text-lg font-light leading-snug">
            A simple starting point for your project.
          </p>
        </div>
        <div className="lg:col-span-7 space-y-6 text-ecoh-muted text-base md:text-lg font-light leading-relaxed">
          <p>
            To provide an initial design and system costing, we simply
            require a set of scaled or measurement drawings for your
            house/unit types or development project.
          </p>
          <p>
            From this, the ecoHeatwave team will prepare a preliminary
            design, including room-by-room heat requirements and an
            outline system specification — giving you a clear
            understanding of how the system integrates and performs
            within your project.
          </p>
          <div className="pt-2">
            <a
              href="#consultation"
              data-testid="design-estimate-cta"
              className="inline-flex items-center text-[13px] font-medium text-ecoh-accent hover:text-ecoh-accentHover border-b border-ecoh-accent/40 hover:border-ecoh-accent pb-1 transition-colors"
            >
              Request your initial design →
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

function SpecAppHeadline() {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
      <div className="lg:col-span-5">
        <h3
          data-testid="design-app-headline"
          className="font-display text-2xl md:text-3xl lg:text-4xl font-light tracking-[-0.02em] leading-[1.1] text-ecoh-ink"
        >
          Whole-home ecoHeatwave Specification App.
        </h3>
        <p className="mt-4 text-ecoh-ink/70 text-base md:text-lg font-light leading-snug">
          An intelligent specification platform for installers,
          designers and heating professionals.
        </p>
      </div>
      <div className="lg:col-span-7 space-y-6 text-ecoh-muted text-base md:text-lg font-light leading-relaxed">
        <p>
          Alternatively, our intelligent specification platform is designed
          specifically to simplify the planning of complete
          ecoHeatwave systems.
        </p>
        <p>
          The app allows users to build a full property specification
          room-by-room and zone-by-zone, creating accurate panel
          layouts tailored to each space. From single-room upgrades
          to full-house installations, the system streamlines the
          design process and ensures every project is specified
          efficiently and correctly.
        </p>
      </div>
    </div>
  );
}

function SpecAppPreview() {
  return (
    <>
      <div
        data-testid="design-app-preview"
        className="mt-12 md:mt-16 rounded-3xl overflow-hidden bg-white ring-1 ring-ecoh-ink/10 shadow-sm"
      >
        <img
          src={APP_PREVIEW_IMG}
          alt="ecoHeatwave specification app — Installer Mode showing the Heating Requirement for the Living Room at 33 Elder Park: 42.75m³ volume, 17.81m² floor area, 1,283W demand, and a design-safe panel length of 11.75m."
          loading="lazy"
          className="w-full h-auto block"
        />
      </div>
      <p className="mt-3 text-xs tracking-[0.2em] uppercase text-ecoh-muted">
        ecoHeatwave Specification App · Installer Mode · Field execution view
      </p>
    </>
  );
}

function SpecAppFeatures() {
  return (
    <div className="mt-12 md:mt-16 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
      <div className="lg:col-span-5">
        <div className="overline mb-4">Key features</div>
      </div>
      <div className="lg:col-span-7">
        <ul
          data-testid="design-app-features"
          className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3"
        >
          {APP_FEATURES.map((f) => (
            <li
              key={f}
              className="flex items-start gap-3 text-ecoh-ink/85 text-[15px] md:text-base font-light"
            >
              <span className="mt-2 inline-block w-1.5 h-1.5 rounded-full bg-ecoh-accent shrink-0" />
              <span>{f}</span>
            </li>
          ))}
        </ul>
        <p className="mt-8 font-medium text-ecoh-ink">
          Whether designing for residential renovations, new builds
          or commercial applications, the ecoHeatwave specification
          app provides a smarter way to configure and manage
          ecoHeatwave installations.
        </p>
      </div>
    </div>
  );
}

function SpecificationApp() {
  return (
    <div className="mt-16 md:mt-20">
      <SpecAppHeadline />
      <SpecAppPreview />
      <SpecAppFeatures />
    </div>
  );
}

function CoverageBand() {
  return (
    <div className="bg-ecoh-surface">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            <h3 className="font-display text-2xl md:text-3xl lg:text-4xl font-light tracking-[-0.02em] leading-[1.1] text-ecoh-ink">
              Around <span className="text-ecoh-accent">30%</span> ceiling coverage. The rest left for design.
            </h3>
          </div>
          <div className="lg:col-span-7 space-y-6 text-ecoh-ink/75 text-base md:text-lg font-light leading-relaxed">
            <p>
              Covering approximately 30% of the ceiling area, the system delivers
              efficient radiant heating while preserving valuable ceiling space
              for lighting design, ventilation strategies, and other essential
              services. This simplifies coordination between trades and reduces
              complexity during both design and installation.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function OffSiteSection() {
  return (
    <div className="py-10">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            <h3 className="font-display text-2xl md:text-3xl lg:text-4xl font-light tracking-[-0.02em] leading-[1.1] text-ecoh-ink">
              Manufactured off-site. Delivered ready to install.
            </h3>
          </div>
          <div className="lg:col-span-7 space-y-6 text-ecoh-muted text-base md:text-lg font-light leading-relaxed">
            <p>
              Each system is manufactured off-site in a controlled environment,
              fully tested, and delivered ready for installation. This approach
              improves programme certainty, and supports a cleaner, more
              efficient build process.
            </p>
            <p className="font-medium text-ecoh-ink">
              An ecoHeatwave system can easily be installed, commissioned and
              up and running in a single day, in a 3–4 bedroom house.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function FutureHomesBand() {
  return (
    <div className="bg-ecoh-surface">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            <h3 className="font-display text-2xl md:text-3xl lg:text-4xl font-light tracking-[-0.02em] leading-[1.1] text-ecoh-ink">
              Aligned with the Future Homes Standard.
            </h3>
          </div>
          <div className="lg:col-span-7 space-y-6 text-ecoh-ink/75 text-base md:text-lg font-light leading-relaxed">
            <p>
              ecoHeatwave has also been developed with the Future Homes Standard
              (FHS) and the Home Energy Model (HEM) in mind. When combined with
              an air source heat pump hot water cylinder, the system supports
              compliance with modern low-carbon building requirements and is
              well aligned with current and future regulatory performance
              expectations.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function LifecycleSection() {
  return (
    <div className="py-10">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            <h3 className="font-display text-2xl md:text-3xl lg:text-4xl font-light tracking-[-0.02em] leading-[1.1] text-ecoh-ink">
              No plant room. No annual servicing.
            </h3>
          </div>
          <div className="lg:col-span-7 space-y-6 text-ecoh-muted text-base md:text-lg font-light leading-relaxed">
            <p>
              With no moving parts, no plant room dependency, and no requirement
              for annual servicing, ecoHeatwave reduces long-term operational
              burden while supporting efficient, low-carbon building strategies
              and modern energy system integration.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function Audience({ label, text }) {
  return (
    <div>
      <div className="text-[11px] tracking-[0.22em] uppercase text-ecoh-amber">{label}</div>
      <p className="mt-3 font-display text-xl md:text-2xl font-light tracking-tight text-white leading-snug">
        {text}
      </p>
    </div>
  );
}

function AudienceTrioBand() {
  return (
    <div className="relative bg-ecoh-ink text-white overflow-hidden">
      <CirclePattern
        className="pointer-events-none select-none absolute inset-0 w-full h-full"
        opacity={0.5}
      />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-ecoh-ink via-ecoh-ink/80 to-ecoh-ink/40" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-12">
          <Audience
            label="For developers"
            text="Faster installation and reduced on-site complexity."
          />
          <Audience
            label="For architects"
            text="Greater freedom in spatial and services design."
          />
          <Audience
            label="For end users"
            text="Reliable, efficient, invisible comfort."
          />
        </div>
      </div>
    </div>
  );
}

// ---------- Default export ---------------------------------------------------

export default function Design() {
  return (
    <section
      id="design"
      data-testid="design-section"
      className="relative overflow-hidden"
    >
      <div className="py-10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <DesignHeader />
          <div className="my-[14mm]" />
          <SpecificationSection />
          <EstimateRequest />
          <SpecificationApp />
        </div>
      </div>

      <CoverageBand />
      <OffSiteSection />
      <FutureHomesBand />
      <LifecycleSection />
      <AudienceTrioBand />
    </section>
  );
}
